import type { Component, MaybeRefOrGetter } from 'vue';
import { computed, markRaw, unref } from 'vue';
import { useSchoolFeatures } from '@/composables/useSchoolFeatures';
import { useSchoolLabels } from '@/composables/useSchoolLabels';
import { can, AppPermissions } from '@/utils/permissions';

import ViewResults from '@/components/DashBoard/Results/ViewResults/ViewResults.vue';
import ResultTemplateList from '@/components/DashBoard/Results/Templates/ResultTemplateList.vue';
import AssessmentTypeManager from '@/components/DashBoard/Results/Assessments/AssessmentTypeManager.vue';
import ScoreEntry from '@/components/DashBoard/Results/ScoreEntry/ScoreEntry.vue';
import BroadsheetManager from '@/components/DashBoard/Broadsheet/BroadsheetManager.vue';
import SummaryManager from '@/components/DashBoard/Summary/SummaryManager.vue';
import CourseTransferManager from '@/components/DashBoard/Results/CourseTransfer/CourseTransferManager.vue';
import TranscriptManager from '@/components/DashBoard/Results/Transcript/TranscriptManager.vue';

import CreateCourse from '@/components/DashBoard/Tertiary/Courses/Course/CreateCourse.vue';
import CreateCurriculum from '@/components/DashBoard/Tertiary/Courses/CreateCurriculum.vue';
import SchoolCourseRegistration from '@/components/DashBoard/Tertiary/Courses/SchoolCourseRegistration.vue';
import CourseExemptions from '@/components/DashBoard/Tertiary/Exemptions/CourseExemptions.vue';
import CourseEquivalence from '@/components/DashBoard/Tertiary/Courses/CourseEquivalence.vue';
import CoursePrerequisites from '@/components/DashBoard/Tertiary/Courses/CoursePrerequisites.vue';
import CourseRegistrationApprovals from '@/components/DashBoard/Tertiary/CourseRegistrationApprovals.vue';

export function useTertiaryDashboard(
  currentUser?: MaybeRefOrGetter<unknown>,
  debugEnabled: MaybeRefOrGetter<boolean> = false,
) {
  const { isTertiary } = useSchoolFeatures();
  const { labels } = useSchoolLabels();

  const termLabel = computed(() => (isTertiary.value ? 'Semester Calendar' : 'Academic Calendar'));
  const classroomTabName = computed(() => (isTertiary.value ? 'Level' : 'Classroom'));

  const courseTabs = computed(() => {
    const userValue = unref(currentUser);
    const tabs: Array<{ name: string; component: Component; query: string }> = [];

    const canViewCoursesAndCurriculum = can(
      userValue,
      AppPermissions.Registrations.Actions.View,
      AppPermissions.Registrations.Resource,
    );
    const canViewApprovals = can(
      userValue,
      AppPermissions.CourseRegistrationApproval.Actions.View,
      AppPermissions.CourseRegistrationApproval.Resource,
    ) || can(
      userValue,
      AppPermissions.CourseRegistrationApproval.Actions.Approve,
      AppPermissions.CourseRegistrationApproval.Resource,
    ) || can(
      userValue,
      AppPermissions.CourseRegistrationApproval.Actions.Endorse,
      AppPermissions.CourseRegistrationApproval.Resource,
    );
    const canViewRegistrations = can(
      userValue,
      AppPermissions.CourseRegistrationApproval.Actions.ViewAll,
      AppPermissions.CourseRegistrationApproval.Resource,
    );

    if (canViewCoursesAndCurriculum) {
      tabs.push({ name: 'Courses', component: markRaw(CreateCourse), query: '' });
      tabs.push({ name: 'Curriculum', component: markRaw(CreateCurriculum), query: 'curriculum' });
    }

    if (canViewApprovals) {
      tabs.push({ name: 'Registration Approvals', component: markRaw(CourseRegistrationApprovals), query: 'approvals' });
    }

    if (canViewRegistrations) {
      tabs.push({ name: 'School Course Registration', component: markRaw(SchoolCourseRegistration), query: 'registration' });
    }

    if (can(userValue, AppPermissions.CourseExemptions.Actions.View, AppPermissions.CourseExemptions.Resource)) {
      tabs.push({ name: 'Exemptions', component: markRaw(CourseExemptions), query: 'exemptions' });
    }

    if (can(userValue, AppPermissions.CourseEquivalence.Actions.View, AppPermissions.CourseEquivalence.Resource)) {
      tabs.push({ name: 'Equivalence', component: markRaw(CourseEquivalence), query: 'equivalence' });
      tabs.push({ name: 'Prerequisites', component: markRaw(CoursePrerequisites), query: 'prerequisites' });
    }

    return tabs;
  });

  const resultsTabs = computed(() => {
    const userValue = unref(currentUser);
    const tabs: Array<{ name: string; component: Component; query: string; icon?: string; group?: string }> = [];

    if (can(userValue, AppPermissions.Results.Actions.UploadScores, AppPermissions.Results.Resource)) {
      tabs.push({ name: 'Score Entry', component: markRaw(ScoreEntry), query: 'score-entry', icon: 'pi-pencil', group: 'entry' });
    }

    if (can(userValue, AppPermissions.Results.Actions.View, AppPermissions.Results.Resource)) {
      tabs.push({ name: 'View Results', component: markRaw(ViewResults), query: 'view', icon: 'pi-eye', group: 'entry' });
    }

    if (can(userValue, AppPermissions.Results.Actions.ViewBroadsheet, AppPermissions.Results.Resource)) {
      tabs.push({ name: 'Broadsheet', component: markRaw(BroadsheetManager), query: 'broadsheet', icon: 'pi-table', group: 'reports' });
    }

    if (can(userValue, AppPermissions.Results.Actions.View, AppPermissions.Results.Resource)) {
      tabs.push({ name: 'Summary', component: markRaw(SummaryManager), query: 'summary', icon: 'pi-list', group: 'reports' });
    }

    if (can(userValue, AppPermissions.ResultTemplates.Actions.View, AppPermissions.ResultTemplates.Resource)) {
      tabs.push({ name: 'Templates', component: markRaw(ResultTemplateList), query: 'templates', icon: 'pi-cog', group: 'setup' });
    }

    if (unref(debugEnabled)) {
      tabs.push({ name: 'Assessment Types', component: markRaw(AssessmentTypeManager), query: 'assessment-types', icon: 'pi-sliders-h', group: 'setup' });
    }

    if (isTertiary.value) {
      if (can(userValue, AppPermissions.Results.Actions.View, AppPermissions.Results.Resource)) {
        tabs.push({ name: 'Course Transfers', component: markRaw(CourseTransferManager), query: 'course-transfers', icon: 'pi-arrow-right-arrow-left', group: 'tertiary' });
      }

      if (can(userValue, AppPermissions.Transcripts.Actions.View, AppPermissions.Transcripts.Resource)) {
        tabs.push({ name: 'Transcript', component: markRaw(TranscriptManager), query: 'transcript', icon: 'pi-file', group: 'tertiary' });
      }
    }

    return tabs;
  });

  return {
    isTertiary,
    labels,
    termLabel,
    classroomTabName,
    courseTabs,
    resultsTabs,
  };
}
